
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Menu, X, Filter, CheckCircle2, Bookmark, Briefcase, Play, Search } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { clearAuthSession, getAuthToken } from '../utils/auth';
import { trackEvent } from '../utils/amplitude';
import CompetencyAnalysis from '../components/interview/CompetencyAnalysis';
import { getDashboardUsage, getInterviewAnalysis } from '../utils/dashboardApi';
import { getSavedJobs } from '../utils/savedJobs';
import PlanBar from '../components/interview/PlanBar';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function formatDateToShort(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function normalizeInterview(interview) {
  return {
    id: interview.id,
    vacancyId: interview.vacancy_id || interview.vacancyId || null,
    title: interview.vacancy_title || 'Interview',
    createdAt: interview.created_at,
    date: formatDateToShort(interview.created_at),
    status: Number(interview.interview_status ?? 0),
    progress: Number(interview.step ?? 0),
  };
}

function getStatusLabel(status) {
  if (status === 2) return 'Completed';
  if (status === 1) return 'In Progress';
  if (status === -1) return 'Saved Vacancy';
  return 'Started';
}

function ItemBadge({ type, status }) {
  if (type === 'vacancy') {
    return (
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
        <Bookmark className="h-5 w-5" />
      </div>
    );
  }
  if (status === 2) {
    return (
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600 border border-green-100">
        <CheckCircle2 className="h-5 w-5" />
      </div>
    );
  }
  return (
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600 border border-orange-100">
      <Play className="h-4 w-4 fill-current" />
    </div>
  );
}

function getUsageProgress(usageData) {
  const used = Number(usageData?.interviews_used ?? 0);
  const limit = Number(usageData?.interviews_limit ?? 0);

  if (limit === -1) {
    return {
      used,
      limit,
      percent: 0,
      exhausted: false,
      label: `${used} / Unlimited`,
    };
  }

  if (limit <= 0) {
    return {
      used,
      limit,
      percent: 0,
      exhausted: false,
      label: `${used} / 0`,
    };
  }

  const percent = Math.min(100, Math.round((used / limit) * 100));
  const exhausted = used >= limit;

  return {
    used,
    limit,
    percent,
    exhausted,
    label: `${used} / ${limit}`,
  };
}

function ProgressRing({ value, max, size = 72, strokeWidth = 5 }) {
  const safeMax = max > 0 ? max : 1;
  const normalized = Math.max(0, Math.min(value, safeMax));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = normalized / safeMax;
  const offset = circumference * (1 - progress);

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="#F97316"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  );
}

const DEFAULT_CAPABILITIES = {
  plan_code: null,
  plan_name: null,
  summary_level: 'medium',
  allow_interview_quick: true,
  allow_interview_standard: true,
  allow_interview_full: true,
  allow_coach_friend: true,
  allow_coach_guide: true,
  allow_coach_challenger: true,
  allow_coach_sergeant: true,
  allow_topic_soft_skills: true,
  allow_topic_hard_skills: true,
  allow_topic_motivation: true,
  allow_topic_behavioral: true,
  allow_topic_ai: true,
  allow_top_skills: true,
  allow_skills_in_progress: true,
  allow_bars_scoring: true,
};

const SUMMARY_DEPTH_LEVELS = ['low', 'medium', 'high'];

const PLACEHOLDER_COVERAGE_MATRIX = {
  1: { score: 3.9, confidence: 0.9 },
  2: { score: 4.1, confidence: 0.9 },
  3: { score: 3.7, confidence: 0.85 },
  4: { score: 4.0, confidence: 0.9 },
  5: { score: 3.8, confidence: 0.86 },
  6: { score: 3.6, confidence: 0.82 },
  7: { score: 3.7, confidence: 0.84 },
  8: { score: 4.2, confidence: 0.92 },
};

function formatDifficulty(format) {
  const value = String(format || '').toLowerCase();
  if (value === 'quick') return 'Quick';
  if (value === 'standard') return 'Standard';
  if (value === 'full') return 'Hard';
  return 'Standard';
}

function normalizeHighlightList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function pickHighlightsSection(highlights, keys) {
  if (!highlights || typeof highlights !== 'object') return [];
  for (const key of keys) {
    const items = normalizeHighlightList(highlights[key]);
    if (items.length) return items;
  }
  return [];
}

function splitDescriptionIntoParagraphs(text) {
  if (!text) return [];
  const normalized = String(text).replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  if (normalized.includes('\n')) {
    return normalized
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }

  const sentences = normalized.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [normalized];
  const grouped = [];
  let buffer = [];

  sentences.forEach((sentence) => {
    const trimmed = sentence.trim();
    if (!trimmed) return;
    buffer.push(trimmed);
    if (buffer.length >= 2) {
      grouped.push(buffer.join(' '));
      buffer = [];
    }
  });

  if (buffer.length) grouped.push(buffer.join(' '));
  return grouped.filter(Boolean);
}

function buildCoverageMatrix(chatHistory) {
  if (!Array.isArray(chatHistory)) return {};

  const coverage = {};
  chatHistory.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const bars = item.bars;
    if (!bars || typeof bars !== 'object') return;

    const scaleId = Number(bars.target_scale ?? bars.scale_id ?? bars.scaleId ?? bars.scale);
    const score = Number(bars.score);
    if (!Number.isFinite(scaleId) || scaleId < 1 || scaleId > 8) return;
    if (!Number.isFinite(score) || score <= 0) return;

    const boundedScore = Math.min(5, Math.max(1, score));
    if (!coverage[scaleId] || boundedScore > Number(coverage[scaleId].score || 0)) {
      coverage[scaleId] = {
        score: boundedScore,
        confidence: Number(bars.confidence ?? 0),
      };
    }
  });

  return coverage;
}

export default function InterviewScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => { trackEvent('myinterview'); }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [interviewsData, setInterviewsData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [plansCatalog, setPlansCatalog] = useState([]);

  const [selectedInterviewId, setSelectedInterviewId] = useState(null);
  const [listFilter, setListFilter] = useState('all');
  const [rightTab, setRightTab] = useState('interview');

  const [interviewAnalyses, setInterviewAnalyses] = useState({});
  const [loadingAnalysisId, setLoadingAnalysisId] = useState(null);
  const interviewAnalysesRef = useRef({});

  const [interviewDetailsById, setInterviewDetailsById] = useState({});
  const [loadingDetailsId, setLoadingDetailsId] = useState(null);
  const [vacancyDetailsByVacancyId, setVacancyDetailsByVacancyId] = useState({});
  const [loadingVacancyId, setLoadingVacancyId] = useState(null);
  const vacancyDetailsRef = useRef({});

  const [expandedQuestionKey, setExpandedQuestionKey] = useState(null);

  // New UI states
  const [isMobileListOpen, setIsMobileListOpen] = useState(false);
  const [savedJobs] = useState(() => getSavedJobs());

  useEffect(() => {
    interviewAnalysesRef.current = interviewAnalyses;
  }, [interviewAnalyses]);
  useEffect(() => {
    vacancyDetailsRef.current = vacancyDetailsByVacancyId;
  }, [vacancyDetailsByVacancyId]);

  const loadInterviews = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile/interviews`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'profile', redirectTo: '/interview' },
        });
        return;
      }

      if (!response.ok) {
        setError('Failed to load interviews');
        return;
      }

      const data = await response.json();
      setInterviewsData(data);
    } catch (loadError) {
      setError('Failed to load interviews');
      console.error('Load interviews error:', loadError);
    }
  }, [navigate]);

  const loadDashboardData = useCallback(async () => {
    try {
      const usage = await getDashboardUsage().catch(() => null);
      setUsageData(usage);
    } catch (loadError) {
      console.error('Failed to load dashboard data:', loadError);
    }
  }, []);

  const loadProfileContext = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'profile', redirectTo: '/interview' },
        });
        return;
      }

      if (!response.ok) return;
      const data = await response.json();
      if (data?.capabilities) {
        setCapabilities((prev) => ({ ...prev, ...data.capabilities }));
      }
    } catch (contextError) {
      console.error('Failed to load profile context:', contextError);
    }
  }, [navigate]);

  const loadCatalogPlans = useCallback(async () => {
    const token = getAuthToken();

    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/plans`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const data = await response.json().catch(() => []);
      if (!response.ok || !Array.isArray(data)) {
        setPlansCatalog([]);
        return;
      }
      setPlansCatalog(data);
    } catch (catalogError) {
      console.error('Failed to load plans catalog:', catalogError);
      setPlansCatalog([]);
    }
  }, []);

  const loadInterviewAnalysis = useCallback(async (interviewId) => {
    if (!interviewId) return null;

    const cached = interviewAnalysesRef.current[interviewId];
    if (cached) return cached;

    try {
      setLoadingAnalysisId(interviewId);
      const analysis = await getInterviewAnalysis(interviewId);
      setInterviewAnalyses((prev) => {
        if (prev[interviewId]) return prev;
        return { ...prev, [interviewId]: analysis };
      });
      return analysis;
    } catch (analysisError) {
      console.error('Failed to load interview analysis:', analysisError);
      return null;
    } finally {
      setLoadingAnalysisId((prev) => (prev === interviewId ? null : prev));
    }
  }, []);
  const loadInterviewDetails = useCallback(async (interviewId) => {
    if (!interviewId) return null;

    const cached = interviewDetailsById[interviewId];
    if (cached) return cached;

    const token = getAuthToken();
    if (!token) return null;

    try {
      setLoadingDetailsId(interviewId);
      const response = await fetch(`${API_BASE_URL}/api/interviews/${interviewId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'profile', redirectTo: '/interview' },
        });
        return null;
      }

      if (!response.ok) {
        return null;
      }

      const details = await response.json();
      setInterviewDetailsById((prev) => ({ ...prev, [interviewId]: details }));
      return details;
    } catch (detailsError) {
      console.error('Failed to load interview details:', detailsError);
      return null;
    } finally {
      setLoadingDetailsId((prev) => (prev === interviewId ? null : prev));
    }
  }, [interviewDetailsById, navigate]);

  const loadVacancyDetails = useCallback(async (vacancyId) => {
    if (!vacancyId) return null;

    const cached = vacancyDetailsRef.current[vacancyId];
    if (cached) return cached;

    const token = getAuthToken();
    if (!token) return null;

    try {
      setLoadingVacancyId(vacancyId);
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const parsedResponse = await fetch(`${API_BASE_URL}/api/jobs_search_requests/parse/result/${vacancyId}`, {
        method: 'GET',
        headers,
      });

      if (parsedResponse.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'profile', redirectTo: '/interview' },
        });
        return null;
      }

      if (parsedResponse.ok) {
        const parsed = await parsedResponse.json();
        const normalized = {
          vacancyId,
          title: parsed?.title || null,
          fullDescription: parsed?.full_description || '',
          highlights: parsed?.highlights || null,
        };
        setVacancyDetailsByVacancyId((prev) => ({ ...prev, [vacancyId]: normalized }));
        return normalized;
      }

      const manualResponse = await fetch(`${API_BASE_URL}/api/vacancies/${vacancyId}`, {
        method: 'GET',
        headers,
      });

      if (manualResponse.status === 401) {
        clearAuthSession();
        navigate('/', {
          replace: true,
          state: { openAuth: true, action: 'profile', redirectTo: '/interview' },
        });
        return null;
      }

      if (!manualResponse.ok) return null;

      const manual = await manualResponse.json();
      const normalized = {
        vacancyId,
        title: manual?.title || null,
        fullDescription: manual?.text || '',
        highlights: null,
      };
      setVacancyDetailsByVacancyId((prev) => ({ ...prev, [vacancyId]: normalized }));
      return normalized;
    } catch (vacancyError) {
      console.error('Failed to load vacancy details:', vacancyError);
      return null;
    } finally {
      setLoadingVacancyId((prev) => (prev === vacancyId ? null : prev));
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadInterviews(),
        loadDashboardData(),
        loadProfileContext(),
        loadCatalogPlans(),
      ]);
      if (!cancelled) {
        setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [loadInterviews, loadDashboardData, loadProfileContext, loadCatalogPlans]);

  const allInterviews = useMemo(
    () => (interviewsData?.recent_interviews || []).map(normalizeInterview),
    [interviewsData]
  );

  const filteredInterviews = useMemo(() => {
    if (listFilter === 'completed') {
      return allInterviews.filter((item) => item.status === 2);
    }
    if (listFilter === 'in_progress') {
      return allInterviews.filter((item) => item.status !== 2);
    }
    return allInterviews;
  }, [allInterviews, listFilter]);

  useEffect(() => {
    if (!allInterviews.length && !savedJobs.length) {
      setSelectedInterviewId(null);
      return;
    }

    const existsInInterviews = allInterviews.some((item) => String(item.id) === String(selectedInterviewId));
    const existsInJobs = savedJobs.some((item) => String(item.id) === String(selectedInterviewId));

    if (!existsInInterviews && !existsInJobs) {
      if (allInterviews.length > 0) {
        setSelectedInterviewId(allInterviews[0].id);
      } else if (savedJobs.length > 0) {
        setSelectedInterviewId(savedJobs[0].id);
        setRightTab('vacancy');
      }
    }
  }, [allInterviews, savedJobs, selectedInterviewId]);

  const selectedInterview = useMemo(() => {
    if (!allInterviews.length && !savedJobs.length) return null;

    const selectedInt = allInterviews.find((item) => String(item.id) === String(selectedInterviewId));
    if (selectedInt) return selectedInt;

    const selectedJob = savedJobs.find((item) => String(item.id) === String(selectedInterviewId));
    if (selectedJob) {
      return {
        id: selectedJob.id,
        vacancyId: selectedJob.id,
        title: selectedJob.title,
        date: formatDateToShort(selectedJob.savedAt),
        status: -1,
        isVacancy: true,
      };
    }

    return allInterviews[0] || (savedJobs[0] ? {
      id: savedJobs[0].id,
      vacancyId: savedJobs[0].id,
      title: savedJobs[0].title,
      date: formatDateToShort(savedJobs[0].savedAt),
      status: -1,
      isVacancy: true,
    } : null);
  }, [allInterviews, savedJobs, selectedInterviewId]);

  useEffect(() => {
    if (!selectedInterview || selectedInterview.status !== 2) return;
    if (interviewAnalyses[selectedInterview.id]) return;
    loadInterviewAnalysis(selectedInterview.id);
  }, [selectedInterview, interviewAnalyses, loadInterviewAnalysis]);

  useEffect(() => {
    if (!selectedInterview) return;
    if (rightTab !== 'qa' && rightTab !== 'interview' && rightTab !== 'overview') return;
    loadInterviewDetails(selectedInterview.id);
  }, [selectedInterview, rightTab, loadInterviewDetails]);

  useEffect(() => {
    if (!selectedInterview?.vacancyId) return;
    if (rightTab !== 'vacancy') return;
    loadVacancyDetails(selectedInterview.vacancyId);
  }, [selectedInterview?.vacancyId, rightTab, loadVacancyDetails]);

  useEffect(() => {
    if (!allInterviews.length) return;
    const missingDetails = allInterviews
      .map((item) => item.id)
      .filter((id) => !interviewDetailsById[id]);
    if (!missingDetails.length) return;
    Promise.all(missingDetails.map((id) => loadInterviewDetails(id))).catch(() => {
      // per-interview errors are handled inside loadInterviewDetails
    });
  }, [allInterviews, interviewDetailsById, loadInterviewDetails]);

  const selectedAnalysis = selectedInterview ? interviewAnalyses[selectedInterview.id] || null : null;
  const selectedDetails = selectedInterview ? interviewDetailsById[selectedInterview.id] || null : null;
  const selectedVacancyDetails = selectedInterview?.vacancyId
    ? vacancyDetailsByVacancyId[selectedInterview.vacancyId] || null
    : null;
  const analysisSummaryText = useMemo(() => {
    if (typeof selectedAnalysis?.summary !== 'string') return '';
    return selectedAnalysis.summary.trim();
  }, [selectedAnalysis?.summary]);

  const finalConclusionText = useMemo(() => {
    if (typeof selectedDetails?.summary !== 'string') return '';
    return selectedDetails.summary.trim();
  }, [selectedDetails?.summary]);

  const usage = useMemo(() => getUsageProgress(usageData), [usageData]);

  const usagePeriodLabel = useMemo(() => {
    if (!usageData?.period_start || !usageData?.period_end) return null;
    const start = new Date(usageData.period_start);
    const end = new Date(usageData.period_end);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }, [usageData]);

  const currentPlan = useMemo(() => {
    const planCode = String(capabilities?.plan_code || '').toLowerCase();
    if (!planCode || !plansCatalog.length) return null;
    return plansCatalog.find((plan) => String(plan?.plan_code || '').toLowerCase() === planCode) || null;
  }, [capabilities?.plan_code, plansCatalog]);

  const difficultyLevels = useMemo(() => {
    if (usageData?.difficulty_levels?.length) {
      return usageData.difficulty_levels;
    }
    return [
      { name: 'Quick', available: Boolean(capabilities?.allow_interview_quick) },
      { name: 'Medium', available: Boolean(capabilities?.allow_interview_standard) },
      { name: 'Hard', available: Boolean(capabilities?.allow_interview_full) },
    ];
  }, [usageData, capabilities]);

  const coachModes = useMemo(() => {
    if (usageData?.coach_modes?.length) {
      return usageData.coach_modes;
    }
    return [
      { id: 'friend', name: 'Friend', available: Boolean(capabilities?.allow_coach_friend) },
      { id: 'guide', name: 'Guide', available: Boolean(capabilities?.allow_coach_guide) },
      { id: 'challenger', name: 'Challenger', available: Boolean(capabilities?.allow_coach_challenger) },
      { id: 'drill_sergeant', name: 'Drill Sergeant', available: Boolean(capabilities?.allow_coach_sergeant) },
    ];
  }, [usageData, capabilities]);

  const summaryLevel = String(capabilities?.summary_level || currentPlan?.summary_level || 'medium').toLowerCase();

  const interviewCoverageMatrix = useMemo(
    () => buildCoverageMatrix(selectedDetails?.chat_history),
    [selectedDetails?.chat_history]
  );
  const hasCoverageData = useMemo(
    () => Object.keys(interviewCoverageMatrix).length > 0,
    [interviewCoverageMatrix]
  );
  const overviewCoverageMatrix = useMemo(
    () => (hasCoverageData ? interviewCoverageMatrix : PLACEHOLDER_COVERAGE_MATRIX),
    [hasCoverageData, interviewCoverageMatrix]
  );
  const isOverviewLocked = useMemo(
    () => !capabilities?.allow_bars_scoring || !hasCoverageData,
    [capabilities?.allow_bars_scoring, hasCoverageData]
  );
  const coveredScalesCount = useMemo(
    () => Object.keys(interviewCoverageMatrix).length,
    [interviewCoverageMatrix]
  );

  const vacancyParagraphs = useMemo(
    () => splitDescriptionIntoParagraphs(selectedVacancyDetails?.fullDescription || ''),
    [selectedVacancyDetails?.fullDescription]
  );
  const vacancyHighlights = selectedVacancyDetails?.highlights || null;
  const vacancyResponsibilities = useMemo(
    () => pickHighlightsSection(vacancyHighlights, ['Responsibilities', 'responsibilities', 'Key Responsibilities', 'Duties']),
    [vacancyHighlights]
  );
  const vacancyRequirements = useMemo(
    () => pickHighlightsSection(vacancyHighlights, ['Qualifications', 'Requirements', 'requirements', 'Skills']),
    [vacancyHighlights]
  );
  const vacancyBenefits = useMemo(
    () => pickHighlightsSection(vacancyHighlights, ['Benefits', 'Perks', 'benefits']),
    [vacancyHighlights]
  );

  const totalQuestions = Number(selectedDetails?.questions_count || 0);
  const currentStep = Number(selectedInterview?.progress || 0);
  const progressPercent = totalQuestions > 0
    ? Math.min(99, Math.max(0, Math.round((currentStep / totalQuestions) * 100)))
    : 0;

  const qaItems = useMemo(() => {
    if (!selectedDetails?.chat_history?.length) return [];
    return selectedDetails.chat_history
      .filter((item) => item && String(item.a || '').trim())
      .sort((a, b) => Number(a?.num || 0) - Number(b?.num || 0));
  }, [selectedDetails]);

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6 sm:space-y-6 sm:px-6 sm:py-8">
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <PlanBar
          planName={currentPlan?.plan_name || capabilities?.plan_name || 'Current plan'}
          usagePeriodLabel={usagePeriodLabel}
          used={usage.used}
          limit={usage.limit}
          difficultyLevels={difficultyLevels}
          coachModes={coachModes}
          allowBars={Boolean(capabilities?.allow_bars_scoring)}
          summaryLevel={summaryLevel}
          onUpgrade={() => navigate('/pricing')}
        />

        <section className={`grid gap-4 ${isDesktop ? 'lg:grid-cols-[minmax(0,33.333%)_minmax(0,66.667%)]' : 'grid-cols-1'}`}>
          {/* Mobile Selector */}
          {!isDesktop && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsMobileListOpen(!isMobileListOpen)}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-orange-200 bg-white px-4 py-4 shadow-sm transition hover:border-orange-300"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                    <Menu className="h-5 w-5" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Selected Interview</p>
                    <p className="truncate text-sm font-bold text-slate-900">
                      {selectedInterview?.title || 'Choose interview...'}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isMobileListOpen ? 'rotate-180' : ''}`} />
              </button>

              {isMobileListOpen && (
                <div className="absolute left-0 right-0 z-30 mt-2 max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3 shadow-xl">
                  <div className="mb-3 flex items-center justify-between px-1">
                    <p className="text-sm font-bold text-slate-900">Select Interview</p>
                    <button
                      type="button"
                      onClick={() => setIsMobileListOpen(false)}
                      className="rounded-full p-1 hover:bg-gray-100"
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="mb-3 grid grid-cols-3 gap-1.5 rounded-xl bg-gray-100 p-1">
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'completed', label: 'Done' },
                      { id: 'in_progress', label: 'Live' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setListFilter(tab.id);
                        }}
                        className={`rounded-lg py-1.5 text-[11px] font-bold transition ${listFilter === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500'}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {filteredInterviews.length > 0 && savedJobs.length > 0 && (
                      <div className="flex items-center gap-2 py-1 px-1">
                        <div className="h-px flex-1 bg-gray-100"></div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">My Interviews</span>
                        <div className="h-px flex-1 bg-gray-100"></div>
                      </div>
                    )}

                    {filteredInterviews.map((item) => {
                      const isSelected = String(selectedInterview?.id) === String(item.id);
                      const isDone = item.status === 2;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setSelectedInterviewId(item.id);
                            setIsMobileListOpen(false);
                            setExpandedQuestionKey(null);
                            setRightTab(isDone ? 'interview' : 'interview');
                          }}
                          className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${isSelected ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-white'}`}
                        >
                          <ItemBadge status={item.status} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-slate-900">{item.title}</p>
                            <p className="truncate text-[11px] text-gray-500">
                              {item.date} • {getStatusLabel(item.status)}
                            </p>
                          </div>
                          {isSelected && <div className="h-2 w-2 rounded-full bg-orange-500" />}
                        </button>
                      );
                    })}

                    {savedJobs.length > 0 && filteredInterviews.length > 0 && (
                      <div className="flex items-center gap-2 py-1 px-1">
                        <div className="h-px flex-1 bg-gray-100"></div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Saved Vacancies</span>
                        <div className="h-px flex-1 bg-gray-100"></div>
                      </div>
                    )}

                    {savedJobs.map((job) => {
                      const isSelected = String(selectedInterview?.id) === String(job.id);
                      return (
                        <button
                          key={job.id}
                          type="button"
                          onClick={() => {
                            setSelectedInterviewId(job.id);
                            setIsMobileListOpen(false);
                            setExpandedQuestionKey(null);
                            setRightTab('vacancy');
                          }}
                          className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${isSelected ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-white'}`}
                        >
                          <ItemBadge type="vacancy" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-slate-900">{job.title}</p>
                            <p className="truncate text-[11px] text-gray-500">
                              {job.company || 'Vacancy'} • {formatDateToShort(job.savedAt)}
                            </p>
                          </div>
                          {isSelected && <div className="h-2 w-2 rounded-full bg-orange-500" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop Sidebar List */}
          {isDesktop && (
            <div className="min-w-0 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="mb-3 grid grid-cols-3 gap-2 rounded-xl bg-gray-100 p-1">
                {[
                  { id: 'all', label: 'All', count: allInterviews.length },
                  { id: 'completed', label: 'Completed', count: allInterviews.filter((item) => item.status === 2).length },
                  { id: 'in_progress', label: 'In Progress', count: allInterviews.filter((item) => item.status !== 2).length },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setListFilter(tab.id)}
                    className={`rounded-lg px-2 py-2 text-xs font-semibold transition ${listFilter === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500'}`}
                  >
                    {tab.label}{' '}
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${listFilter === tab.id ? 'bg-slate-900 text-white' : 'bg-gray-300 text-white'}`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {!filteredInterviews.length && !savedJobs.length ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 text-center">
                  <Briefcase className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  {loading ? 'Loading...' : 'No items yet.'}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {filteredInterviews.length > 0 && savedJobs.length > 0 && (
                    <div className="flex items-center gap-2 py-1 px-1">
                      <div className="h-px flex-1 bg-gray-100"></div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">My Interviews</span>
                      <div className="h-px flex-1 bg-gray-100"></div>
                    </div>
                  )}

                  {filteredInterviews.map((item) => {
                    const isSelected = String(selectedInterview?.id) === String(item.id);
                    const isDone = item.status === 2;
                    const itemDetails = interviewDetailsById[item.id] || null;
                    const difficultyLabel = formatDifficulty(itemDetails?.interview_format);

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSelectedInterviewId(item.id);
                          setExpandedQuestionKey(null);
                          setRightTab(isDone ? 'interview' : 'interview');
                        }}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${isSelected ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      >
                        <ItemBadge status={item.status} />

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{item.title}</p>
                          <p className="truncate text-[11px] text-gray-500">
                            {item.date || 'No date'} • {getStatusLabel(item.status)} • {difficultyLabel}
                          </p>
                        </div>

                        <span className={`rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-tight ${isDone ? 'border border-green-200 bg-green-50 text-green-700' : 'border border-orange-200 bg-orange-50 text-orange-700'}`}>
                          {isDone ? 'Done' : `Step ${Math.max(0, item.progress)}`}
                        </span>
                      </button>
                    );
                  })}

                  {savedJobs.length > 0 && filteredInterviews.length > 0 && (
                    <div className="flex items-center gap-2 py-2 px-1">
                      <div className="h-px flex-1 bg-gray-100"></div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Saved Vacancies</span>
                      <div className="h-px flex-1 bg-gray-100"></div>
                    </div>
                  )}

                  {savedJobs.map((job) => {
                    const isSelected = String(selectedInterview?.id) === String(job.id);
                    return (
                      <button
                        key={job.id}
                        type="button"
                        onClick={() => {
                          setSelectedInterviewId(job.id);
                          setExpandedQuestionKey(null);
                          setRightTab('vacancy');
                        }}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${isSelected ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      >
                        <ItemBadge type="vacancy" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{job.title}</p>
                          <p className="truncate text-[11px] text-gray-500">
                            {job.company || 'Vacancy'} • {formatDateToShort(job.savedAt)}
                          </p>
                        </div>
                        <span className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase tracking-tight text-blue-700">
                          Saved
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50">
              {[
                { id: 'interview', label: 'Interview Details' },
                { id: 'qa', label: 'Q&A Review' },
                { id: 'vacancy', label: 'Vacancy Details' },
                { id: 'overview', label: 'Overall Progress' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setRightTab(tab.id)}
                  className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition ${rightTab === tab.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {rightTab === 'overview' && (
              <div className="p-4 sm:p-6">
                {!selectedInterview ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Select an interview to view overall progress.
                  </div>
                ) : selectedInterview.isVacancy ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                    <Filter className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-semibold text-slate-900">Competency Progress Locked</p>
                    <p className="mt-1">Complete an AI interview to generate your competency heatmap and skill analysis.</p>
                  </div>
                ) : loadingDetailsId === selectedInterview.id && !selectedDetails ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Loading competency analysis...
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Interview Competency Analysis</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedInterview.title}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {selectedInterview.date || 'No date'} • Covered scales: {coveredScalesCount}/8
                      </p>
                    </div>

                    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white">
                      <div className={isOverviewLocked ? 'select-none blur-[3px]' : ''}>
                        <CompetencyAnalysis coverageMatrix={overviewCoverageMatrix} locked={false} />
                      </div>

                      {isOverviewLocked ? (
                        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-[4px] p-4">
                          <div className="max-w-md rounded-2xl border border-orange-200 bg-white/95 px-5 py-4 text-center shadow-sm">
                            <p className="text-sm font-semibold text-slate-900">
                              Competency analytics are unavailable for this interview on your current plan.
                            </p>
                            <p className="mt-1 text-xs text-gray-600">
                              Upgrade your plan to unlock BARS-based interview analysis.
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            )}

            {rightTab === 'interview' && (
              <div className="p-4 sm:p-6">
                {!selectedInterview ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Select an interview or vacancy to view details.
                  </div>
                ) : selectedInterview.isVacancy ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                        Saved Vacancy
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedInterview.title}</p>
                      <p className="mt-1 text-xs text-gray-500">Saved on {selectedInterview.date || 'No date'}</p>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                      <p className="text-sm text-gray-600 mb-4">
                        You have saved this vacancy. You can now start an AI-powered interview to practice your skills for this specific role.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate(`/chat-setting/${selectedInterview.id}`)}
                        className="btn-orange w-full py-2.5 text-sm font-semibold sm:w-auto sm:px-6"
                      >
                        Start AI Interview
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className={`rounded-xl border p-4 ${selectedInterview.status === 2 ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                      <p className={`text-xs font-semibold uppercase tracking-wide ${selectedInterview.status === 2 ? 'text-green-700' : 'text-orange-700'}`}>
                        {selectedInterview.status === 2 ? 'Interview completed' : 'Interview in progress'}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{selectedInterview.title}</p>
                      <p className="mt-1 text-xs text-gray-500">{selectedInterview.date || 'No date'} • {getStatusLabel(selectedInterview.status)}</p>
                    </div>

                    {selectedInterview.status === 2 ? (
                      loadingAnalysisId === selectedInterview.id && !selectedAnalysis ? (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                          Loading interview report...
                        </div>
                      ) : selectedAnalysis ? (
                        <div className="space-y-4">
                          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Final Conclusion</p>
                            <p className="mt-2 whitespace-pre-line text-sm text-slate-800">
                              {finalConclusionText || analysisSummaryText || 'Interview completed. Detailed summary will appear once generated.'}
                            </p>
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="rounded-xl border border-green-200 bg-green-50 p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Strengths</p>
                              {selectedAnalysis.strengths?.length ? (
                                <ul className="mt-2 space-y-1 text-sm text-green-800">
                                  {selectedAnalysis.strengths.map((item) => (
                                    <li key={`${selectedInterview.id}-s-${item}`}>• {item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="mt-2 text-sm text-green-800">No strengths provided yet.</p>
                              )}
                            </div>

                            <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">Growth Areas</p>
                              {selectedAnalysis.improvements?.length ? (
                                <ul className="mt-2 space-y-1 text-sm text-purple-800">
                                  {selectedAnalysis.improvements.map((item) => (
                                    <li key={`${selectedInterview.id}-i-${item}`}>• {item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="mt-2 text-sm text-purple-800">No growth areas provided yet.</p>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              const state = { vacancyTitle: selectedInterview.title };
                              if (selectedInterview.vacancyId) state.vacancyId = selectedInterview.vacancyId;
                              navigate(`/chat/${selectedInterview.id}`, { state });
                            }}
                            className="btn-orange w-full py-2.5 text-sm font-semibold sm:w-auto sm:px-5"
                          >
                            View Full Interview
                          </button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                          Detailed interview report is not available yet.
                        </div>
                      )
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                            <span>Progress</span>
                            <span>{totalQuestions > 0 ? `${progressPercent}%` : `Step ${Math.max(0, currentStep)}`}</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                            <div
                              className="h-full rounded-full bg-orange-500"
                              style={{ width: `${totalQuestions > 0 ? progressPercent : 20}%` }}
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const state = { vacancyTitle: selectedInterview.title };
                            if (selectedInterview.vacancyId) state.vacancyId = selectedInterview.vacancyId;
                            navigate(`/chat/${selectedInterview.id}`, { state });
                          }}
                          className="btn-orange w-full py-2.5 text-sm font-semibold sm:w-auto sm:px-5"
                        >
                          Continue Interview
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {rightTab === 'vacancy' && (
              <div className="p-4 sm:p-6">
                {!selectedInterview ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Select an interview to view vacancy details.
                  </div>
                ) : !selectedInterview.vacancyId ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Vacancy details are unavailable for this interview.
                  </div>
                ) : loadingVacancyId === selectedInterview.vacancyId && !selectedVacancyDetails ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Loading vacancy details...
                  </div>
                ) : !selectedVacancyDetails ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Detailed vacancy description is not available yet.
                  </div>
                ) : (
                  <div className="space-y-4">
                    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Vacancy</p>
                      <h3 className="mt-1 text-lg font-semibold text-slate-900">
                        {selectedVacancyDetails.title || selectedInterview.title}
                      </h3>
                      <p className="mt-1 break-all text-xs text-gray-400">{selectedInterview.vacancyId}</p>
                    </section>

                    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                      <h4 className="text-base font-semibold text-slate-900">About the Role</h4>
                      {vacancyParagraphs.length ? (
                        <div className="mt-3 space-y-3 text-sm leading-relaxed text-gray-700">
                          {vacancyParagraphs.map((paragraph, index) => (
                            <p key={`${selectedInterview.vacancyId}-p-${index}`}>{paragraph}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-gray-600">Detailed description is not available for this vacancy.</p>
                      )}
                    </section>

                    {vacancyResponsibilities.length ? (
                      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                        <h4 className="text-base font-semibold text-slate-900">Responsibilities</h4>
                        <ul className="mt-3 space-y-2 text-sm text-gray-700">
                          {vacancyResponsibilities.map((item) => (
                            <li key={`${selectedInterview.vacancyId}-r-${item}`} className="flex items-start gap-2">
                              <span className="mt-0.5 text-orange-500">&#10003;</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}

                    {vacancyRequirements.length ? (
                      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                        <h4 className="text-base font-semibold text-slate-900">Requirements</h4>
                        <ul className="mt-3 space-y-2 text-sm text-gray-700">
                          {vacancyRequirements.map((item) => (
                            <li key={`${selectedInterview.vacancyId}-q-${item}`} className="flex items-start gap-2">
                              <span className="mt-0.5 text-orange-500">&#10003;</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}

                    {vacancyBenefits.length ? (
                      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
                        <h4 className="text-base font-semibold text-slate-900">Benefits & Perks</h4>
                        <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                          {vacancyBenefits.map((item) => (
                            <div key={`${selectedInterview.vacancyId}-b-${item}`} className="rounded-lg bg-[#F6F7FB] px-3 py-2">
                              {item}
                            </div>
                          ))}
                        </div>
                      </section>
                    ) : null}

                    <div className="flex pt-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/chat-setting/${selectedInterview.vacancyId}`)}
                        className="btn-orange w-full py-3 text-sm font-semibold sm:w-auto sm:px-8"
                      >
                        Start AI Interview
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {rightTab === 'qa' && (
              <div className="p-4 sm:p-6">
                {!selectedInterview ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Select an interview to view Q&A review.
                  </div>
                ) : selectedInterview.isVacancy ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
                    <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-semibold text-slate-900">No Q&A data available</p>
                    <p className="mt-1">You need to start and complete an interview for this vacancy to see the Q&A review.</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/chat-setting/${selectedInterview.id}`)}
                      className="btn-orange mt-4 py-2 px-6 text-xs font-bold"
                    >
                      Start Practicing
                    </button>
                  </div>
                ) : loadingDetailsId === selectedInterview.id && !selectedDetails ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Loading Q&A review...
                  </div>
                ) : !qaItems.length ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Complete at least one answer to unlock Q&A review.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {qaItems.map((item, index) => {
                      const key = `${selectedInterview.id}-${item.num || index}`;
                      const isOpen = expandedQuestionKey === key;
                      const barsScore = typeof item?.bars?.score === 'number' ? item.bars.score : null;

                      return (
                        <article
                          key={key}
                          className={`overflow-hidden rounded-xl border transition ${isOpen ? 'border-green-300 bg-green-50/40' : 'border-gray-200 bg-white'}`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedQuestionKey((prev) => (prev === key ? null : key))}
                            className="flex w-full items-center gap-3 px-3 py-3 text-left sm:px-4"
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-xs font-semibold text-green-700">
                              {item.num || index + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-900">Question {item.num || index + 1}</p>
                              <p className="truncate text-xs text-gray-500">{(item.q || '').slice(0, 90) || 'Interview question'}</p>
                            </div>
                            {barsScore !== null ? (
                              <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-slate-700">
                                {barsScore.toFixed(1)}/5
                              </span>
                            ) : null}
                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {isOpen && (
                            <div className="space-y-3 border-t border-gray-100 px-3 py-3 sm:px-4 sm:py-4">
                              <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700">Question</p>
                                <p className="mt-1 text-sm text-slate-800">{item.q || 'No question text.'}</p>
                              </div>

                              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Your Answer</p>
                                <p className="mt-1 text-sm text-slate-800">{item.a || 'No answer saved.'}</p>
                              </div>

                              <div className="rounded-lg border border-purple-100 bg-purple-50 px-3 py-2.5">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-700">AI Feedback</p>
                                <p className="mt-1 text-sm text-slate-800">{item.f || 'Feedback will appear after processing.'}</p>
                              </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

